/* eslint-disable @typescript-eslint/no-explicit-any */
/* ────────────────────────────────────────────────────────────────────────
   SalaryStructureFormPage — the shared Add/Edit SCREEN (routed, not a modal).

   One component serves both modes via the `mode` prop. On edit the record is
   passed through router state; if it is missing (hard refresh) we return to the
   list. Sections render from the config map (per template); the yup resolver is
   rebuilt whenever the structure type changes so only the active type's fields
   are validated or submitted. `structureType` is persisted on save.
   ──────────────────────────────────────────────────────────────────────── */
import React, { useMemo, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaArrowLeft } from "react-icons/fa";
import Button from "components/Button";
import apiService from "services/apiService";
import { useToaster } from "contexts/toasterContext";

import StructureTypePicker from "./StructureTypePicker";
import SummaryPanel from "./SummaryPanel";
import { StructureType, SectionId, sectionsForType, TYPE_CARDS, defaultsFor } from "./salaryStructure.config";
import { schemaFor } from "./salaryStructure.schema";
import { formToPayload, recordToForm, inferStructureType } from "./salaryStructure.mapper";

import BasicInfoSection from "./sections/BasicInfoSection";
import EmployeeSalarySection from "./sections/EmployeeSalarySection";
import EmployeeAllowancesSection from "./sections/EmployeeAllowancesSection";
import EmployeeDeductionsSection from "./sections/EmployeeDeductionsSection";
import ContractorContractSection from "./sections/ContractorContractSection";
import TraineeCompensationSection from "./sections/TraineeCompensationSection";
import TraineeBenefitsSection from "./sections/TraineeBenefitsSection";

const SECTION_COMPONENTS: Record<SectionId, React.FC> = {
  basic: BasicInfoSection,
  empSalary: EmployeeSalarySection,
  empAllowances: EmployeeAllowancesSection,
  empDeductions: EmployeeDeductionsSection,
  contractDetails: ContractorContractSection,
  traineeComp: TraineeCompensationSection,
  traineeBenefits: TraineeBenefitsSection,
};

const LIST_PATH = "/admin/salaryStructure";

// Sections hidden per structure type. Trainee and Intern share the TRAINEE
// template (basic → traineeComp → traineeBenefits), but the Benefits section
// must not be shown for them, so it is stripped from the rendered list here.
const HIDDEN_SECTIONS_BY_TYPE: Partial<Record<StructureType, SectionId[]>> = {
  TRAINEE: ["traineeBenefits"],
  INTERN: ["traineeBenefits"],
};

const SalaryStructureFormPage = ({ mode }: { mode: "create" | "edit" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToaster();
  const isEdit = mode === "edit";

  const record = (location.state as { record?: any } | null)?.record ?? null;

  const [structureType, setStructureType] = useState<StructureType>(
    isEdit ? inferStructureType(record) : "PERMANENT_EMPLOYEE"
  );

  const resolver = useMemo(() => yupResolver(schemaFor(structureType)), [structureType]);
  const initialValues = useMemo(
    () => (isEdit ? recordToForm(record) : defaultsFor("PERMANENT_EMPLOYEE")),
    [isEdit, record]
  );

  const methods = useForm({ resolver, mode: "onChange", defaultValues: initialValues as any });
  const { isDirty, isSubmitting, isValid, errors } = methods.formState;
  const errorCount = Object.keys(errors).length;

  // No record to edit (e.g. a hard refresh lost router state) — bounce to list.
  if (isEdit && !record) {
    return <Navigate to={LIST_PATH} replace />;
  }

  const goBack = () => navigate(LIST_PATH);

  const handleTypeChange = (next: StructureType) => {
    if (next === structureType) return;
    const nextLabel = TYPE_CARDS.find((c) => c.type === next)?.title ?? "the new type";
    if (
      methods.formState.isDirty &&
      // eslint-disable-next-line no-alert
      !window.confirm(`Fields that do not apply to ${nextLabel} will be cleared. Continue?`)
    ) {
      return;
    }
    const { structureName, description } = methods.getValues() as any;
    setStructureType(next);
    methods.reset({ ...defaultsFor(next), structureName, description } as any);
  };

  const onValid = (data: any) => {
    const payload = formToPayload({ ...data, structureType }, isEdit ? record : undefined);
    const req = isEdit
      ? apiService.put(`/api/salary/updateSalaryStructure`, payload)
      : apiService.post(`api/salary/addSalaryStructure`, payload);
    return req
      .then(() => {
        addToast(isEdit ? "Salary Structure updated successfully" : "Salary Structure added successfully", "success");
        navigate(LIST_PATH);
      })
      .catch((error: any) => {
        addToast(
          error?.response?.data || (isEdit ? "Error updating salary structure" : "Salary Structure addition failed"),
          "error"
        );
      });
  };

  const onInvalid = (formErrors: any) => {
    const first = Object.keys(formErrors)[0];
    if (first) {
      try {
        methods.setFocus(first as any);
      } catch {
        /* segmented controls aren't focusable via setFocus — ignore */
      }
    }
  };

  // Trainee/Intern structures should not expose the optional Benefits section
  // ("traineeBenefits"), so it is filtered out of the rendered sections for
  // those two types. All other sections for the template are kept as-is.
  const sections = sectionsForType(structureType).filter(
    (id) => !(HIDDEN_SECTIONS_BY_TYPE[structureType] ?? []).includes(id)
  );

  return (
    <div className="text-slate-900 dark:text-white bg-slate-100 dark:bg-gray-900 min-h-screen transition-colors duration-300">
      {/* Screen header */}
      <div className="flex items-center gap-3 px-4 md:px-6 py-4">
        <button
          onClick={goBack}
          className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-slate-50 dark:hover:bg-gray-700"
          title="Back to Salary Structures"
        >
          <FaArrowLeft className="w-3.5 h-3.5" />
        </button>
        <div>
          <h1 className="text-lg font-bold">{isEdit ? "Edit Salary Structure" : "Add Salary Structure"}</h1>
          <p className="text-[12px] text-slate-500 dark:text-slate-400">
            Configure a reusable compensation structure.
          </p>
        </div>
      </div>

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onValid, onInvalid)} className="px-4 md:px-6">
          <div className="mb-5">
            <StructureTypePicker value={structureType} onChange={handleTypeChange} locked={isEdit} />
          </div>

          {/* Explicit grid tracks: form column may shrink (minmax 0,1fr), the
              summary column is a fixed 340px. Prevents the panel from taking
              full width and overlapping the form. */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6 items-start">
            <div className="min-w-0 space-y-5">
              {sections.map((id) => {
                const SectionComp = SECTION_COMPONENTS[id];
                return <SectionComp key={id} />;
              })}
            </div>
            <SummaryPanel structureType={structureType} />
          </div>

          {/* Sticky action bar */}
          <div className="sticky bottom-0 mt-8 -mx-4 md:-mx-6 z-10 bg-white dark:bg-gray-800 border-t border-slate-200 dark:border-gray-700 px-4 md:px-6 h-[74px] flex items-center justify-end gap-3">
            {!isValid && errorCount > 0 && (
              <span className="text-[12px] text-red-600 font-semibold">
                Fix {errorCount} error{errorCount === 1 ? "" : "s"} to save
              </span>
            )}
            <Button
              type="button"
              onClick={goBack}
              className="!bg-white !text-slate-600 border border-slate-200 hover:!bg-slate-100 focus:!ring-4 focus:!outline-none focus:!ring-slate-200 transition-all active:scale-95"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isDirty || isSubmitting || !isValid}
              className="!text-white !bg-blue-700 hover:!bg-blue-800 focus:!ring-4 focus:!outline-none focus:!ring-blue-300 disabled:!opacity-50 disabled:!cursor-not-allowed"
            >
              {isEdit ? "Save Changes" : "Create Structure"}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default SalaryStructureFormPage;
